import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';

// --- Lens Descriptions ---

const transcriptDescriptions = {
  grounded_emergence:
    'Identifies and categorizes key themes within a transcript, providing a structured overview of main topics discussed, their frequency, and representative quotes.',
  custom_lens:
    'Analyzes transcripts based on user-defined coding frameworks or lenses, applying custom instructions to extract relevant themes, patterns, and insights as specified by the user.',

  equity_audit:
    'Analyzes transcripts through the lens of equity (Access, Inclusion, Representation, Fairness, Safety, Trust), scoring dimensions, identifying themes and gaps, and providing evidence-based recommendations.',
  framing_discourse:
    'Deconstructs how participants frame key ideas in transcripts, identifying dominant frames, the discursive strategies used (e.g., metaphors, tone), and supporting quotes.',
  journey_pain:
    'Maps user or customer journey stages as described in transcripts, identifying specific pain points, their underlying causes, severity, and supporting participant quotes for each stage.',
  jtbd_map:
    "Distills functional, emotional, and social 'Jobs To Be Done' from transcripts, mapping the triggers, struggles, desired outcomes, and supporting quotes associated with each job.",
  narrative_arc:
    'Identifies and analyzes narrative arcs (stories of change or experience) within transcripts, outlining the beginning, turning point, resolution, and emotional journey with supporting quotes.',
  outcome_barrier:
    'Maps desired participant outcomes against the barriers encountered (systemic, informational, emotional, etc.) based on transcript evidence, identifying common barriers and high-leverage opportunities.',
  persona_task:
    'Develops proto-personas from transcripts by mapping their motivations and context to the specific tasks they attempt, including the steps taken, friction points, enablers, and outcomes.',
  phenomenological:
    'Explores the lived experience described in transcripts by distilling its essential themes, the sensory and emotional qualities of the experience, and its meaning to the participant.',
  reflexivity_tracker:
    'Examines researcher influence on the conversation in transcripts, identifying moments (signals) where researcher positionality or prompts may have shaped participant responses and data quality.',
  resonance_scan:
    'Scans transcripts to evaluate how specific messages land with participants, identifying which messages resonate positively (affirming, emotional connection) and which are missed (confusion, indifference, resistance).',
  saturation_gauge:
    'Assesses the conceptual saturation level within a transcript by analyzing the repetition of ideas, emergence of novel concepts, contradictions, and convergence to gauge if more data collection is needed.',
  sentiment_spectrum:
    'Analyzes the full spectrum of sentiment and emotion expressed in transcripts, identifying the overall positive/neutral/negative distribution, specific emotional hotspots, and narrative emotional arcs.',
  toc_alignment:
    "Maps how elements described in the transcript align (or don't) with a predefined Theory of Change, identifying confirmations, gaps, and emergent factors.",
};

const overallDescriptions = {
  grounded_emergence:
    'Aggregates transcript-level thematic analyses to identify overarching themes, their prevalence across transcripts, inter-theme relationships, and portfolio-level insights.',
  custom_lens:
    'Synthesizes findings from transcript-level custom lens analyses to identify common patterns, cross-cutting themes, and strategic insights at the portfolio level.',
  equity_audit:
    'Synthesizes multiple transcript-level equity audits into a portfolio-level assessment, calculating aggregate scores, identifying dimension trends, recurring themes/gaps, and priority actions across transcripts.',
  framing_discourse:
    'Integrates transcript-level framing analyses to identify dominant frames, their prevalence, common discursive strategies, and representative quotes across a dataset, providing portfolio-level insights.',
  journey_pain:
    'Synthesizes transcript-level journey maps into a portfolio view, identifying common stages, their prevalence, recurring pain points/causes, dominant severities, and priority insights across user experiences.',
  jtbd_map:
    'Aggregates transcript-level JTBD maps to provide a portfolio view of key functional, emotional, and social jobs, their prevalence, common triggers/struggles/outcomes, and strategic insights.',
  narrative_arc:
    'Synthesizes transcript-level narrative arc analyses to identify dominant arcs, their prevalence, common emotional journeys, representative quotes, and portfolio-level strategic insights.',
  outcome_barrier:
    'Integrates transcript-level outcome-barrier maps into a portfolio view, identifying prevalent outcomes and barriers, barrier type distribution, high-leverage opportunities, and priority insights across participants.',
  persona_task:
    'Synthesizes transcript-level persona-task maps into a portfolio view, outlining prevalent personas and tasks, common motivations, friction points, enablers, and priority strategic insights.',
  phenomenological:
    'Integrates transcript-level phenomenological analyses into a portfolio-level depiction of lived experience, identifying prevalent themes, common qualities, representative meanings, and priority insights.',
  reflexivity_tracker:
    'Synthesizes transcript-level reflexivity findings into a portfolio view, identifying patterns in researcher positionality, common effects on data, notable signals, and priority recommendations to mitigate bias.',
  resonance_scan:
    'Combines transcript-level resonance scans into a portfolio view, identifying overall resonant vs. missed messages, reaction type distributions, prevalent message responses, and priority communication insights.',
  saturation_gauge:
    'Integrates transcript-level saturation gauges to assess overall conceptual saturation across a dataset, identifying unique concepts, signal distributions, underrepresented perspectives, and data collection recommendations.',
  sentiment_spectrum:
    'Synthesizes transcript-level sentiment spectra into a portfolio view, showing aggregate sentiment distribution, top emotion hotspots, common emotional arcs, and priority insights for action.',
  toc_alignment:
    'Synthesizes transcript-level ToC (Theory of Change) alignment maps into a portfolio view, quantifying aligned, misaligned, and emergent elements, assessing prevalence, and identifying priority implications for the theory.',
};

// --- Component ---

const AnalysisLensHelperText = ({ lensName, level, size = 'small' }) => {
  if (!lensName) {
    return null; // Don't render if no lens name provided
  }

  let titleText = '';
  const overallDesc = overallDescriptions[lensName] || 'No overall description available.';
  const transcriptDesc =
    transcriptDescriptions[lensName] || 'No transcript-level description available.';

  switch (level) {
    case 'transcript':
      titleText = transcriptDesc;
      break;
    case 'overall':
      titleText = overallDesc;
      break;
    case 'run':
      titleText = (
        <Box>
          <Typography variant="body2" paragraph sx={{ mb: 1 }}>
            <strong>Overall Goal:</strong> {overallDesc}
          </Typography>
          <Typography variant="body2">
            <strong>How it works per transcript:</strong> {transcriptDesc}
          </Typography>
        </Box>
      );
      break;
    default:
      console.warn(`AnalysisLensHelperText received unknown level: ${level}`);
      titleText = 'Unknown analysis level specified.';
      break;
  }

  return (
    <Tooltip title={titleText} arrow placement="top">
      {/* Added span because Tooltip needs a DOM element child when the child might be disabled or null */}
      <span>
        <IconButton
          size={size}
          sx={{ p: 0.5, ml: 0.5, color: 'text.secondary' }}
          aria-label={`Info about ${lensName.replace(/_/g, ' ')} lens`}
        >
          <InfoOutlinedIcon fontSize="inherit" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

AnalysisLensHelperText.propTypes = {
  /** The snake_case identifier for the analysis lens */
  lensName: PropTypes.string.isRequired,
  /** The context in which the helper text is displayed ('transcript', 'overall', 'run') */
  level: PropTypes.oneOf(['transcript', 'overall', 'run']).isRequired,
  /** The size of the icon button */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

export default AnalysisLensHelperText;
